/**
 * Generic in-memory TTL cache for API responses.
 *
 * ponytail: Map + timestamp, no deps. Deduplicates in-flight requests so
 * two components mounting simultaneously share one fetch. 5-minute default
 * TTL covers the entire session between admin config changes.
 */

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  expiry: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

/**
 * Fetch with cache. If a fresh entry exists, returns it immediately.
 * If the same key is already being fetched, shares the in-flight promise
 * instead of making a duplicate request.
 */
export async function cached<T>(key: string, fetcher: () => Promise<T>, ttlMs = DEFAULT_TTL_MS): Promise<T> {
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (entry && entry.expiry > Date.now()) return entry.data;

  // Deduplicate concurrent requests for the same key
  const pending = inflight.get(key) as Promise<T> | undefined;
  if (pending) return pending;

  const promise = fetcher().then((data) => {
    cache.set(key, { data, expiry: Date.now() + ttlMs });
    inflight.delete(key);
    return data;
  }).catch((err) => {
    inflight.delete(key);
    throw err;
  });

  inflight.set(key, promise);
  return promise;
}

/** Evict a single cache key. */
export function invalidate(key: string): void {
  cache.delete(key);
}

/** Evict all keys matching a prefix (e.g. 'ticket-fields' clears all field option caches). */
export function invalidatePrefix(prefix: string): void {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

/** Nuclear option — clear the entire cache. Called on logout or admin saves. */
export function invalidateAll(): void {
  cache.clear();
}
